import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractColumn from "./AbstractColumn.js";
import ActualsPeriodColumnSource from "./periodColumnSource/ActualsPeriodColumnSource.js";
import CustomPeriod from "../../../period/CustomPeriod.js";
import LabelsDimensionSpecificationVisitor from "../../../dimensionSpecification/visitors/LabelsDimensionSpecificationVisitor.js";
import periodColumnSourceFactory from "./periodColumnSource/periodColumnSourceFactory.js";
import ShortTextRepresentationDimensionSpecificationVisitor from "../../../dimensionSpecification/visitors/ShortTextRepresentationDimensionSpecificationVisitor.js";

/**
 * Abstract period report column.
 *
 * @abstract
 * @param {period} spec.period - period of the column
 * @param {AbstractPeriodColumnSource} [spec.source] - Represents where
 *   the column's data is coming from (e.g., actual accounting data or
 *   budget)
 * @param {specification} [spec.dimensionSpecification] - Dimension specification if this is a dimension column
 * @param {dimension} [spec.dimensionTree] - Root dimension from
 * which dimension ids are resolved. Mandatory if `dimensionSpecification`
 * is not empty.
 */
class AbstractPeriodColumn extends AbstractColumn {
	constructor({
		period,
		source = new ActualsPeriodColumnSource(),
		isEditableBudgetColumn = false,
		dimensionSpecification,
		dimensionTree,
	} = {}) {
		super(...arguments);
		this._period = period;
		this._source = source;
		this._isEditableBudgetColumn = isEditableBudgetColumn;
		this._dimensionSpecification = dimensionSpecification;
		this._dimensionTree = dimensionTree;
	}

	getPeriod() {
		return this._period;
	}

	getBudgetId() {
		return this._source.getBudgetId();
	}

	getForecastEstimateId() {
		return this._source.getForecastEstimateId();
	}

	getDimensionSpecification() {
		return this._dimensionSpecification;
	}

	getShortDimensionText() {
		if (!this._dimensionSpecification) {
			return "";
		}

		const visitor =
			new ShortTextRepresentationDimensionSpecificationVisitor({
				dimensionTree: this._dimensionTree,
			});
		return this._dimensionSpecification.accept(visitor);
	}

	getDimensionLabels() {
		if (!this._dimensionSpecification) {
			return [];
		}

		const visitor = new LabelsDimensionSpecificationVisitor({
			dimensionTree: this._dimensionTree,
		});
		return this._dimensionSpecification.accept(visitor);
	}

	getBudget() {
		return (
			this._source.isBudgetSource() &&
			this.candidateBudgets().find(
				(budget) => budget.getId() === this._source.getBudgetId(),
			)
		);
	}

	getBudgetName() {
		const budget = this.getBudget();
		return budget ? budget.getName() : _("No budget");
	}

	getDimensionTree() {
		return this._dimensionTree;
	}

	candidateBudgets() {
		return this._period.getYear().getBudgets();
	}

	validate({ collector }) {
		super.validate(...arguments);

		collector.addWarningIf(
			this._period.isCustomPeriod(),
			_("Period column refers to non existing data"),
		);

		collector.addWarningIf(
			this._missesDimensionOpeningBalances(),
			_(
				"Dimension opening balances for year {year} were not imported. Please re-import this year.",
				{
					year: this._period.getYear().toSmartString(),
				},
			),
		);
	}

	hasBudget() {
		return !!this.getBudget();
	}

	isBudgetColumn() {
		return this._source.isBudgetSource();
	}

	isForecastEstimateColumn() {
		return this._source.isForecastEstimateSource();
	}

	isEditableBudgetColumn() {
		return this._isEditableBudgetColumn;
	}

	isForecastColumn() {
		return this._source.isForecastSource();
	}

	hasChoiceForBudget() {
		return this.isBudgetColumn() && this.candidateBudgets().length > 1;
	}

	hasSettings() {
		return this.hasChoiceForBudget() || this.hasOwnDimensionSpecification();
	}

	isPeriodColumn() {
		return true;
	}

	isBalanceColumn() {
		return false;
	}

	hasOwnDimensionSpecification() {
		return !!this._dimensionSpecification;
	}

	accept(visitor) {
		return visitor.visitAbstractPeriodColumn(this);
	}

	_getDefaultTitle() {
		return this._period.toSmartString();
	}

	_missesDimensionOpeningBalances() {
		return (
			this._needsDimensionOpeningBalances() &&
			!this._fiscalYearHasDimensionOpeningBalances()
		);
	}

	_needsDimensionOpeningBalances() {
		return (
			this.isBalanceColumn() &&
			(this._report.hasDimensionRow() ||
				this._report.hasDimensionSpecification() ||
				this.hasOwnDimensionSpecification())
		);
	}

	_fiscalYearHasDimensionOpeningBalances() {
		return this._period.getYear().hasDimensionOpeningBalances();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.period = this.getPeriod().toString();
		properties.isEditableBudgetColumn = this._isEditableBudgetColumn;
		properties.dimensionSpecification = this._dimensionSpecification;

		return { ...properties, ...this._source.serialize() };
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		const periodData = data.period;
		data.period = context.findPeriodByIdWithinCompany(periodData);

		if (!data.period || data.period.isNullPeriod()) {
			data.period = context
				.getFiscalYear()
				.getAllPeriods()
				.find((period) => period.toSmartString() === periodData);

			data.period ||= new CustomPeriod({ label: periodData });
		}

		data.source = periodColumnSourceFactory.materialize(data, context);
	}
}

export default AbstractPeriodColumn;
